import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { apiClient } from '../services/apiClient';

const Wrap = styled.div`
  max-width: 560px;
  margin: 40px auto;
  padding: 0 20px;
`;
const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: 14px;
  padding: 28px;
`;
const Badge = styled.span<{ $ok: boolean }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $ok }) => ($ok ? '#DCFCE7' : '#FEF2F2')};
  color: ${({ $ok }) => ($ok ? '#166534' : '#991B1B')};
`;

interface Status { active: boolean; status: string; currentPeriodEnd: string | null; isStaff: boolean; reason?: string | null }

export function SubscriptionPage() {
  const [info, setInfo] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    apiClient.get<Status>('/api/me/subscription')
      .then(setInfo)
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Wrap>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Subscription</h1>
      <p style={{ color: theme.colors.textMuted, marginBottom: 20 }}>Coopen SMM plan — $20 / month.</p>
      <Card>
        {loading ? (
          <p style={{ color: theme.colors.textMuted }}>Loading…</p>
        ) : info?.isStaff ? (
          <>
            <Badge $ok>Staff access</Badge>
            <p style={{ marginTop: 12, color: theme.colors.textSecondary }}>Your account (admin/mod) has full access without a subscription.</p>
          </>
        ) : info?.reason === 'collaborator' ? (
          <>
            <Badge $ok>Collaborator access</Badge>
            <p style={{ marginTop: 12, color: theme.colors.textSecondary }}>
              You're in for free as a collaborator on a shared workspace — you don't pay a subscription.
              Access depends on the owner keeping their plan active.
            </p>
          </>
        ) : (
          <>
            <Badge $ok={!!info?.active}>{info?.active ? 'Active' : 'Inactive'}</Badge>
            <div style={{ marginTop: 14, color: theme.colors.textSecondary, fontSize: 14, lineHeight: 1.7 }}>
              <div>Status: <strong>{info?.status || '—'}</strong></div>
              {info?.currentPeriodEnd && (
                <div>Next renewal: <strong>{new Date(info.currentPeriodEnd).toLocaleDateString()}</strong></div>
              )}
            </div>
            <div style={{ marginTop: 16, fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.7 }}>
              <strong>Your plan includes:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: theme.colors.textMuted }}>
                <li>AI post generation (text + image) for 11 networks</li>
                <li>All AIs included — no need to bring your own keys</li>
                <li>Templates, Styles, AI-styled posters and Image to Image</li>
                <li>Gallery and export (ZIP + CSV)</li>
              </ul>
            </div>
            <p style={{ marginTop: 16, fontSize: 13, color: theme.colors.textMuted }}>
              To manage or cancel your subscription, go to your{' '}
              <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noreferrer">PayPal → Automatic payments</a>.
              Cancel anytime and keep access until the end of the paid period.
            </p>
          </>
        )}
      </Card>
    </Wrap>
  );
}
